<template>
  <div
    class="room-card rounded pa-1 mb-1 elevation-1"
    :class="{ 'room-card--compact': compact }"
    :data-id="room.id"
    :style="{
      ...heatmapStyle,
      '--card-color': color || '#1576c7'
    }"
  >
    <div
      class="room-card-name font-weight-medium"
      :class="{ 'room-card-name--compact': compact }"
      :style="{ color: color || 'inherit' }"
    >
      {{ room.name }}
    </div>

    <template v-if="!compact">
      <!-- Price Display with Bar -->
      <div class="price-display-container" v-if="hasRange">
        <!-- Floating Label for Avg Price -->
        <div class="avg-price-label" :style="{ left: markerPosition + '%' }">
          <div class="label-content">
            {{ formatPrice(room.price_avg) }}
          </div>
          <div class="label-arrow"></div>
        </div>

        <!-- Bar -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div class="price-bar-container" v-on="on" v-bind="attrs">
              <div class="price-bar-track"></div>
              <div
                class="price-bar-marker"
                :style="{ left: markerPosition + '%' }"
              ></div>
            </div>
          </template>
          <div class="d-flex flex-column align-center">
            <div>{{ $t("average") }}: {{ formatPrice(room.price_avg) }}</div>
            <div class="text-caption grey--text text--lighten-2">
              {{ $t("min") }}: {{ formatPrice(room.price_min) }} -
              {{ $t("max") }}: {{ formatPrice(room.price_max) }}
            </div>
          </div>
        </v-tooltip>
      </div>

      <!-- Fallback if no range (single price) -->
      <div
        v-else-if="room.price_avg"
        class="text-caption grey--text text--darken-1 d-flex align-center mt-1"
        style="gap: 5px"
      >
        {{ formatPrice(room.price_avg) }}
      </div>
    </template>
  </div>
</template>

<script>
import numeral from "numeral";

export default {
  name: "aRoomTypeCard",
  props: {
    room: {
      type: Object,
      required: true
    },
    heatmapStyle: {
      type: Object,
      default: () => ({})
    },
    color: {
      type: String,
      default: null
    },
    currencySymbol: {
      type: String,
      default: "€"
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    hasRange() {
      return (
        this.room.price_min != null &&
        this.room.price_max != null &&
        parseFloat(this.room.price_min) !== parseFloat(this.room.price_max)
      );
    },
    markerPosition() {
      if (!this.hasRange || this.room.price_avg == null) return 50;
      const min = parseFloat(this.room.price_min);
      const max = parseFloat(this.room.price_max);
      const avg = parseFloat(this.room.price_avg);

      if (max === min) return 50;

      const percentage = ((avg - min) / (max - min)) * 100;
      return Math.max(0, Math.min(100, percentage));
    }
  },
  methods: {
    formatPrice(value) {
      return numeral(value).format("0,0") + this.currencySymbol;
    }
  }
};
</script>

<style lang="scss" scoped>
$secondary: #1576c7;

.room-card {
  cursor: grab;
  background-color: white;
  padding-bottom: 8px !important;

  &--compact {
    padding-bottom: 2px !important;
    padding-top: 2px !important;
  }

  .room-card-name {
    font-size: 0.75rem !important;
    line-height: 0.9rem;
    letter-spacing: 0 !important;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 1.8rem;
    margin-bottom: 2px;

    &--compact {
      -webkit-line-clamp: 1;
      height: auto;
      margin-bottom: 0;
      display: block;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
}

.price-display-container {
  position: relative;
  margin-top: 24px; // Space for the floating label
}

.avg-price-label {
  position: absolute;
  top: -22px; // Position above the bar
  transform: translateX(-50%);
  z-index: 2;
  pointer-events: none; // Let clicks pass through to the tooltip activator if needed

  .label-content {
    background-color: var(--card-color, $secondary);
    color: white;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: bold;
    white-space: nowrap;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    line-height: 1.2;
  }

  .label-arrow {
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 4px solid var(--card-color, $secondary);
  }
}

.price-bar-container {
  position: relative;
  height: 6px;
  width: 100%;
  cursor: help;
}

.price-bar-track {
  width: 100%;
  height: 100%;
  background: var(--card-color, $secondary);
  border-radius: 3px;
  opacity: 1;
}

.price-bar-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 10px;
  height: 10px;
  background-color: white;
  border: 2px solid var(--card-color, $secondary);
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  z-index: 1;
}
</style>
